import type { ReactNode } from 'react';
import { FileText } from 'lucide-react';

/**
 * The single-scope estimate HERO — a framed "document" card that opens the
 * customer estimate, shared by the public quote and the portal. It reads like a
 * real quotation: a left label column ("Estimate" + number, under a document
 * icon) divided from the body, the scope title, a one-line lead (headline price
 * + short summary), a status beside the title, and a facts row — service address, issue date, and
 * how long the quote holds.
 *
 * Presentational only: every value is resolved by the surface and passed in.
 */
export function SingleScopeHero({
  estimateNumber,
  docLabel = 'Estimate',
  preparedFor,
  statusPill,
  validNote,
  title,
  lead,
  facts,
}: {
  /** Document number shown under the tile label, e.g. "EST-0003" / "PROP-0001". */
  estimateNumber?: string;
  /** Tile label — "Estimate" by default; a proposal passes "Proposal". */
  docLabel?: string;
  /** The customer the quote is for — the first fact. */
  preparedFor?: ReactNode;
  statusPill?: ReactNode;
  /** Quiet note shown beneath the "Valid until" date, e.g. "13 days left". */
  validNote?: ReactNode;
  title: string;
  /** One-line scope lead under the title (headline price + short summary). */
  lead?: ReactNode;
  /** Label/value pairs shown in the facts row, after the customer. */
  facts: Array<{ label: string; value: ReactNode }>;
}) {
  // Keep the facts grid sized for its values; the status belongs beside the title.
  const cellCount = (preparedFor ? 1 : 0) + facts.length;
  const lgColsClass = cellCount >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4';
  return (
    <div className="overflow-hidden rounded-[10px] border border-[#14141A]/[0.08] bg-white">
      <div className="flex flex-col sm:flex-row">
        {/* Left label column — document icon over "Estimate" + number.
            Matches the artifact's .dochead__tile: 170px, faint tint, divider. */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[#14141A]/[0.08] p-[22px] sm:w-[170px] sm:flex-col sm:items-start sm:justify-between sm:border-b-0 sm:border-r sm:bg-[#FBFBFA]">
          <FileText className="size-[30px] text-[#95969D]" strokeWidth={1.5} />
          <div className="text-right sm:mt-12 sm:text-left">
            <p className="text-lg font-medium tracking-[-0.02em] text-[#17171A]">{docLabel}</p>
            {estimateNumber ? (
              <p className="mt-0.5 text-[11.5px] font-medium tracking-[0.04em] text-[#95969D]">
                {estimateNumber}
              </p>
            ) : null}
          </div>
        </div>

        {/* Body — title, lead, and the facts row. */}
        <div className="min-w-0 flex-1 px-6 py-[22px]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h1 className="min-w-0 flex-1 basis-40 text-pretty text-[23px] font-normal leading-[1.08] tracking-[-0.026em] text-[var(--ss-primary)] [font-family:var(--ss-heading-font)]">
              {title}
            </h1>
            {statusPill ? <div className="ml-auto max-w-full shrink-0">{statusPill}</div> : null}
          </div>
          {lead ? (
            <div className="mt-2 max-w-2xl text-[13.5px] leading-[1.55] text-[#63646B]">{lead}</div>
          ) : null}

          <dl
            className={`mt-[18px] grid grid-cols-2 gap-[15px] border-t border-[#14141A]/[0.08] pt-4 ${lgColsClass}`}
          >
            {preparedFor ? (
              <div className="min-w-0">
                <dt className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#95969D]">
                  Prepared for
                </dt>
                <dd className="mt-1.5 break-words text-[14px] font-medium tracking-[-0.012em] text-[#17171A]">
                  {preparedFor}
                </dd>
              </div>
            ) : null}
            {facts.map((fact) => {
              const isValidUntil = /valid until/i.test(fact.label);
              return (
                <div key={fact.label} className="min-w-0">
                  <dt className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#95969D]">
                    {fact.label}
                  </dt>
                  <dd className="mt-1.5 break-words text-[14px] font-medium tracking-[-0.012em] text-[#17171A]">
                    {fact.value}
                    {isValidUntil && validNote ? (
                      <span className="block text-[#95969D]">{validNote}</span>
                    ) : null}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    </div>
  );
}
